import { FC, useContext, useState, useEffect, Suspense, lazy } from 'react';
import { LoginProvider, LoginContext } from './contexts/LoginContext';
import { Outlet } from 'react-router';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom';
import './App.css';
import { trackPageview } from './analytics';
import Root from './routes/root';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy-loaded route components for code-splitting
const Login = lazy(() => import('./routes/login'));
const Search = lazy(() => import('./routes/search'));
const Users = lazy(() => import('./routes/users'));
const User = lazy(() => import('./routes/user'));
const UserTradeActivity = lazy(() => import('./routes/userTradeActivity'));
const Home = lazy(() => import('./routes/home'));
const HowTo = lazy(() => import('./routes/howto'));
const Auth = lazy(() => import('./routes/auth'));
const Terms = lazy(() => import('./routes/tos'));
const Privacy = lazy(() => import('./routes/tos'));
const About = lazy(() => import('./routes/about'));
const Donate = lazy(() => import('./routes/donate'));
const Logout = lazy(() => import('./routes/logout'));
const Profile = lazy(() => import('./routes/profile'));
const Index = lazy(() => import('./routes/index'));
const Beer = lazy(() => import('./routes/beer'));
const Beers = lazy(() => import('./routes/beers'));
const Directories = lazy(() => import('./routes/directories'));
const Brewery = lazy(() => import('./routes/brewery'));
const Trades = lazy(() => import('./routes/trades'));
const TradeView = lazy(() => import('./routes/trade'));
const OfferView = lazy(() => import('./routes/offer'));
const Collection = lazy(() => import('./routes/collection'));
const UserCollection = lazy(() => import('./routes/userCollection'));
const CsvImport = lazy(() => import('./routes/csvimport'));
const VerifyImports = lazy(() => import('./routes/verifyimports'));
const NotFound = lazy(() => import('./routes/notfound'));

const LoadingFallback = () => (
  <div
    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
  >
    <div className="spinner-border" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const NotFoundView = () => <div>Not Found View</div>;

// One Google Analytics pageview per route, standing in for the document loads
// the server-rendered site reported.
const PageviewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
};

const SessionProvider = () => {
  const { loggedInUntappdUser, loggedInUser, untappdAccessToken, logOutUser, isLoggedIn } =
    useContext(LoginContext);
  const [data, setData] = useState(loggedInUser);

  useEffect(() => {
    console.log('SessionProvider isLoggedIn? ', isLoggedIn && isLoggedIn());
    setData(loggedInUser);
  }, [isLoggedIn]);

  const logout = async () => {
    logOutUser && (await logOutUser());
  };

  return (
    <>
      <Outlet context={{ data, login: setData, logout }} />
      {/* After the Outlet so the route has already set document.title, which is
          what Analytics reads as the page title. */}
      <PageviewTracker />
    </>
  );
};

const router = createBrowserRouter([
  {
    element: <SessionProvider />,
    children: [
      {
        path: '/',
        element: <Root title="BeerXchange" />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Index title="BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/login',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Login />
              </Suspense>
            ),
          },
          {
            path: '/search',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Search title="Search | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/beer/:id',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Beer title="Beer | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/beer/:beerslug/:id',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Beer title="Beer | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/beers',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Beers title="Beers | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/directories',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Directories title="Beer Directory | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/directories/:letter',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Directories title="Beer Directory | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/brewery/:id',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Brewery title="Brewery | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/brewery/:breweryslug/:id',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Brewery title="Brewery | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/users',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Users title="Users | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/user/:username',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<User title="User | BeerXchange" />} />
              </Suspense>
            ),
          },
          {
            path: '/trades/:username',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute
                  element={<UserTradeActivity title="Trade Activity | BeerXchange" />}
                />
              </Suspense>
            ),
          },
          {
            path: '/howto',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <HowTo title="HowTo | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/authenticate',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Auth />
              </Suspense>
            ),
          },
          {
            path: '/tos',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Terms title="Terms | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/terms',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Terms title="Terms | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/privacy',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Terms title="Privacy | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/about',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <About title="About | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/donate',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Donate title="Donate | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/trades',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Trades title="Trades | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/trade/:id',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<TradeView title="Trade | BeerXchange" />} />
              </Suspense>
            ),
          },
          {
            path: '/logout',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Logout />
              </Suspense>
            ),
          },
          {
            path: '/profile',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<Profile title="Profile | BeerXchange" />} />
              </Suspense>
            ),
          },
          {
            path: '/home/:username',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<Home title="Home | BeerXchange" />} />
              </Suspense>
            ),
          },
          {
            path: '/collection',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<Collection title="Collection | BeerXchange" />} />
              </Suspense>
            ),
          },
          {
            path: '/collection/import',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<CsvImport title="Import Collection | BeerXchange" />} />
              </Suspense>
            ),
          },
          {
            path: '/collection/import/verify',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<VerifyImports title="Verify Imports | BeerXchange" />} />
              </Suspense>
            ),
          },
          {
            path: '/collection/:name',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <UserCollection title="Collection | BeerXchange" />
              </Suspense>
            ),
          },
          {
            path: '/offer/:username',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute element={<OfferView title="Make Offer | BeerXchange" />} />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NotFound title="BeerXchange" />
          </Suspense>
        ),
      },
    ],
  },
]);

interface Props {}

const App: FC<Props> = () => {
  return (
    <LoginProvider>
      <RouterProvider router={router} />
    </LoginProvider>
  );
};

export default App;
